import { CoverInformation } from "../api/quote/types";
import { Quote, QuoteRequest } from "../types";

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
    }
}

let initializedKey: string | null = null;

export function parseMeasurementIds(configValue: string | undefined | null): string[] {
    if (!configValue?.trim()) return [];
    return configValue
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
}

export function initGoogleAnalytics(
    measurementIds: string[],
    options?: { debug?: boolean }
): void {
    if (!measurementIds.length) return;

    const key = measurementIds.join(",");
    if (initializedKey === key) return;
    initializedKey = key;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementIds[0])}`;
    document.head.appendChild(script);

    window.gtag("js", new Date());

    for (const id of measurementIds) {
        window.gtag("config", id, {
            send_page_view: false,
            ...(options?.debug ? { debug_mode: true } : {}),
        });
    }
}

export function trackQuotePageView(pagePath: string): void {
    if (!window.gtag) return;

    const pageSlug = pagePath.replace(/\/+$/, "").split("/").pop() || "quote";

    window.gtag("event", "page_view", {
        page_path: pagePath,
        page_title: pageSlug,
    });
}

export type PurchaseEventParams = {
    transactionId: string;
    value: number;
    currency: string;
    baseCoverName: string;
    baseCoverValue: number;
};

export function trackPurchase(params: PurchaseEventParams): void {
    if (!window.gtag) return;

    window.gtag("event", "purchase", {
        transaction_id: params.transactionId,
        value: params.value,
        currency: params.currency,
        items: [
            {
                item_name: params.baseCoverName,
                item_category: "insurance",
                price: params.baseCoverValue,
                quantity: 1,
            },
        ],
    });
}

export function getBaseCoverForAnalytics(
    quote: Quote,
    quoteRequest: QuoteRequest,
    baseProducts: CoverInformation[] | null | undefined
): { name: string; premiumMajorUnits: number } {
    const coverType = quoteRequest?.desiredCoverTypes?.[0];
    const baseProduct = baseProducts?.find((p) => p.coverType === coverType);
    const cover =
        quote?.coverList?.find((c) => c.coverType === coverType) ??
        quote?.coverList?.find((c) => c.mandatory) ??
        quote?.coverList?.[0];

    const name = baseProduct?.name ?? cover?.name ?? coverType ?? "insurance";
    const premiumPence = cover?.premium ?? 0;

    return {
        name,
        premiumMajorUnits: premiumPence / 100,
    };
}
